/**
 * CreditsSection — section "Mes crédits Life" intégrée dans le panneau
 * Abonnement (UserOptionCard/Abonnement.js).
 *
 * Charte visuelle alignée sur le composant Abonnement :
 *   - Card elevation={0} + border #E5E7EB
 *   - NAVY (#001B42) pour les titres / valeurs primaires
 *   - ACCENT (#2563EB) pour le bouton d'achat
 *   - Stack avec spacing constant
 *   - i18n namespace "layout" (même que le reste du panneau)
 */
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import BoltRoundedIcon from '@mui/icons-material/BoltRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded'
import ReplayCircleFilledRoundedIcon from '@mui/icons-material/ReplayCircleFilledRounded'
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import UndoRoundedIcon from '@mui/icons-material/UndoRounded'
import moment from 'moment'

import {
  selectTransactions,
  selectWallet,
} from '../../reduxLib/slices/billingSlice'
import { loadWallet } from './billingThunks'
import CreditStore from './CreditStore'

const NAVY = '#001B42'
const ACCENT = '#2563EB'
const BORDER = '#E5E7EB'

const KIND_ICON = {
  consume: RemoveCircleOutlineRoundedIcon,
  cycle_refill: ReplayCircleFilledRoundedIcon,
  pack_purchase: ShoppingCartCheckoutRoundedIcon,
  admin_adjust: TuneRoundedIcon,
  refund: UndoRoundedIcon,
  expiry: RemoveCircleOutlineRoundedIcon,
}

function formatNumber(n) {
  return new Intl.NumberFormat('fr-FR').format(Number(n || 0))
}

export default function CreditsSection() {
  const dispatch = useDispatch()
  const { t } = useTranslation('layout')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const wallet = useSelector(selectWallet)
  const transactions = useSelector(selectTransactions)
  const [storeOpen, setStoreOpen] = useState(false)

  useEffect(() => {
    dispatch(loadWallet())
  }, [dispatch])

  const kindLabels = {
    consume: t('settings.credits.kind.consume', 'Consommation IA'),
    cycle_refill: t('settings.credits.kind.cycle_refill', 'Recharge mensuelle'),
    pack_purchase: t('settings.credits.kind.pack_purchase', 'Achat de pack'),
    admin_adjust: t('settings.credits.kind.admin_adjust', 'Ajustement'),
    refund: t('settings.credits.kind.refund', 'Remboursement'),
    expiry: t('settings.credits.kind.expiry', 'Expiration'),
  }

  // Ratio quota d'abonnement utilisé (subscription_credits restants / quota).
  const subscriptionRatio = useMemo(() => {
    if (!wallet?.planQuota || wallet.planQuota <= 0) return 0
    return Math.max(
      0,
      Math.min(1, (wallet?.subscriptionCredits || 0) / wallet.planQuota),
    )
  }, [wallet?.subscriptionCredits, wallet?.planQuota])

  const progressColor =
    subscriptionRatio < 0.05
      ? 'error'
      : subscriptionRatio < 0.2
        ? 'warning'
        : 'success'

  return (
    <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, mb: 3 }}>
      <CardContent>
        {/* Header : titre + CTA d'achat ------------------------------- */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="space-between"
          mb={2.5}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <BoltRoundedIcon sx={{ color: NAVY }} />
            <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 17 }}>
              {t('settings.credits.title', 'Mes crédits Life')}
            </Typography>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setStoreOpen(true)}
            sx={{
              textTransform: 'none',
              bgcolor: ACCENT,
              fontWeight: 600,
              '&:hover': { bgcolor: '#1D4ED8' },
            }}
          >
            {t('settings.credits.buyCredits', 'Acheter des crédits')}
          </Button>
        </Stack>

        {/* Solde principal ------------------------------------------- */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems={isMobile ? 'flex-start' : 'flex-end'}
          spacing={2}
          mb={2.5}
        >
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography sx={{ fontSize: 40, fontWeight: 700, color: NAVY, lineHeight: 1 }}>
              {formatNumber(wallet?.total)}
            </Typography>
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              {t('settings.credits.available', 'crédits disponibles')}
            </Typography>
          </Stack>
          <Box ml={isMobile ? 0 : 'auto'} textAlign={isMobile ? 'left' : 'right'}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: 11,
              }}
            >
              {t('settings.credits.lastRefill', 'Dernière recharge')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: NAVY, mt: 0.25 }}>
              {wallet?.lastRefillAt
                ? moment(wallet.lastRefillAt).format('LL')
                : '—'}
            </Typography>
          </Box>
        </Stack>

        {/* Barre de progression du quota mensuel --------------------- */}
        <Box mb={3}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
            <Typography sx={{ fontSize: 13, color: NAVY, fontWeight: 500 }}>
              {t('settings.credits.monthlyQuota', 'Quota mensuel restant')}
            </Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {formatNumber(wallet?.subscriptionCredits)} /{' '}
              {formatNumber(wallet?.planQuota)}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={subscriptionRatio * 100}
            color={progressColor}
            sx={{ height: 8, borderRadius: 4, bgcolor: '#EEF1F5' }}
          />
        </Box>

        {/* Détail subscription vs purchased -------------------------- */}
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Box
            flex={1}
            sx={{
              p: 2,
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              minHeight: 92,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: 11,
              }}
            >
              {t('settings.credits.subscriptionCredits', 'Inclus dans l\u2019abonnement')}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: NAVY, mt: 0.5 }}>
              {formatNumber(wallet?.subscriptionCredits)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {t('settings.credits.subscriptionHint', 'remis à zéro au prochain cycle')}
            </Typography>
          </Box>
          <Box
            flex={1}
            sx={{
              p: 2,
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              minHeight: 92,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: 11,
              }}
            >
              {t('settings.credits.purchasedCredits', 'Crédits achetés')}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: NAVY, mt: 0.5 }}>
              {formatNumber(wallet?.purchasedCredits)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
              {t('settings.credits.purchasedHint', 'n\u2019expirent jamais')}
            </Typography>
          </Box>
        </Stack>

        {/* Historique des mouvements --------------------------------- */}
        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={1.25} alignItems="center" mb={1.5}>
          <HistoryRoundedIcon sx={{ color: NAVY, fontSize: 20 }} />
          <Typography sx={{ fontWeight: 600, fontSize: 15, color: NAVY }}>
            {t('settings.credits.history', 'Historique des crédits')}
          </Typography>
          {transactions?.length > 0 && (
            <Chip
              size="small"
              label={transactions.length}
              sx={{
                ml: 1,
                bgcolor: '#EEF1F5',
                color: NAVY,
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
        </Stack>

        {!transactions || transactions.length === 0 ? (
          <Box
            sx={{
              border: `1px dashed ${BORDER}`,
              borderRadius: '8px',
              p: 3,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t(
                'settings.credits.noHistory',
                'Aucun mouvement pour le moment. Utilisez l\u2019IA Life ou achetez un pack pour démarrer.',
              )}
            </Typography>
          </Box>
        ) : (
          <Stack
            divider={<Divider flexItem sx={{ borderColor: '#F0F0F0' }} />}
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: '8px',
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {transactions.map((tx) => {
              const Icon = KIND_ICON[tx.kind] || RemoveCircleOutlineRoundedIcon
              const isDebit = tx.amount < 0
              return (
                <Stack
                  key={tx.id}
                  direction={isMobile ? 'column' : 'row'}
                  spacing={isMobile ? 0.5 : 2}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  px={2}
                  py={1.25}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
                    <Icon
                      sx={{
                        color: isDebit ? '#C62828' : '#2E7D32',
                        opacity: 0.9,
                        fontSize: 22,
                      }}
                    />
                    <Box flex={1} minWidth={0}>
                      <Typography sx={{ fontSize: 14, color: NAVY }}>
                        {kindLabels[tx.kind] || tx.kind}
                        {tx.related_feature && (
                          <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                            · {tx.related_feature}
                          </Box>
                        )}
                        {tx.pack_label && (
                          <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                            · {tx.pack_label}
                          </Box>
                        )}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {moment(tx.created_at).format('LLL')}
                      </Typography>
                    </Box>
                  </Stack>
                  <Stack
                    spacing={0}
                    alignItems={isMobile ? 'flex-start' : 'flex-end'}
                    ml={isMobile ? 4.5 : 0}
                  >
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: isDebit ? '#C62828' : '#2E7D32',
                      }}
                    >
                      {tx.amount > 0 ? '+' : ''}
                      {formatNumber(tx.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('settings.credits.balanceAfter', 'solde : {{n}}', {
                        n: formatNumber(tx.balance_after),
                      })}
                    </Typography>
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        )}
      </CardContent>

      <CreditStore open={storeOpen} onClose={() => setStoreOpen(false)} />
    </Card>
  )
}

// Petit chip réutilisable pour les badges "Modèle économique / premium"
// dans d'autres écrans qui voudraient l'afficher.
export function CreditTierChip({ tier = 'economic' }) {
  const isPremium = tier === 'premium'
  const Icon = isPremium ? AutoAwesomeRoundedIcon : BoltRoundedIcon
  return (
    <Chip
      size="small"
      icon={<Icon style={{ fontSize: 14 }} />}
      label={isPremium ? 'Premium' : 'Économique'}
      sx={{
        bgcolor: '#EEF1F5',
        color: NAVY,
        fontWeight: 500,
        '.MuiChip-icon': { color: NAVY },
      }}
    />
  )
}
